import { useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiRequest } from './msalConfig';

/**
 * Hook that returns a function which acquires an access token for our API.
 *
 * Why a function instead of returning the token directly?
 * The token expires (~1 hour). We want callers to ask for a fresh one right
 * before each request, not capture a stale token in a closure. acquireTokenSilent
 * caches transparently — if the cached token is still valid, this is cheap.
 *
 * The InteractionRequiredAuthError fallback triggers a popup re-auth when the
 * refresh token has expired (e.g. user came back the next day). For an internal
 * app on a corporate network, this is rare enough that a popup is fine; we'll
 * revisit if it becomes annoying.
 */
export function useAccessToken(): () => Promise<string> {
  const { instance, accounts } = useMsal();

  // Memoized so the returned function has a STABLE identity across renders.
  // Consumers put this in useEffect dependency arrays (e.g. attachment image
  // fetches) — a fresh function each render caused an infinite fetch loop.
  return useCallback(async () => {
    const account = accounts[0] ?? instance.getActiveAccount();
    if (!account) {
      throw new Error('No signed-in account. Did you forget to redirect to /login?');
    }

    try {
      const result = await instance.acquireTokenSilent({ ...apiRequest, account });
      return result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        const result = await instance.acquireTokenPopup({ ...apiRequest, account });
        return result.accessToken;
      }
      throw err;
    }
  }, [instance, accounts]);
}
